import { useState, useCallback, useContext } from "react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Check, ChevronsUpDown, ExternalLink, Sunset } from "lucide-react";
import { Link } from "@/components/links";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Plug as NoUserIcon, User as UserCheckIcon } from "lucide-react";
import { shorten, cn, formatNumber, findListingByLstId } from "@/lib/utils";
import { useWallet } from "@txnlab/use-wallet";
import AccountSheetContext from "@/providers/account-sheet";
import { CopyButton } from "./copy-button";
import { WithErrorAndLoading } from "./with-error-and-loading";
import { Token } from "./token";
import { SingleToken } from "@/components/single-token";
import { twMerge } from "tailwind-merge";
import { useAlgoAcctInfo, useBalance } from "@/hooks/use-algo-account";
import { useNFDAvatar, useReverseNFD, useReverseNFDs } from "@/hooks/use-nfd";
import { useListings } from "@/hooks/use-listings";

interface AccountsDropdownProps {
  value: string;
  addresses: string[];
  setActive: (addr: string) => void;
}

export function AccountsDropdown({
  value: selectedAddress,
  addresses,
  setActive,
}: AccountsDropdownProps) {
  const [open, setOpen] = useState(false);

  const { data: selectedNFD } = useReverseNFD(selectedAddress);
  const displaySelected = selectedNFD
    ? `${selectedNFD} (${shorten(selectedAddress, 9, false)})`
    : shorten(selectedAddress, 6, true);

  const { data: addrWithNFD } = useReverseNFDs(addresses);
  const addressesObj = !addrWithNFD
    ? []
    : addrWithNFD.map(([address, nfd]) => ({
        label:
          nfd === null
            ? shorten(address, 6, true)
            : `${nfd} (${shorten(address, 9, false)})`,
        value: address,
      }));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-80 justify-between"
        >
          {displaySelected}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-80">
        <Command>
          <CommandGroup>
            {addressesObj.map((address) => (
              <CommandItem
                key={address.value}
                value={address.value}
                onSelect={(currentValue) => {
                  if (currentValue !== selectedAddress) setActive(currentValue);
                  setOpen(false);
                }}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    selectedAddress === address.value
                      ? "opacity-100"
                      : "opacity-0"
                  )}
                />
                {address.label}
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

interface ModalProps {
  closeCb: () => void;
}

function ConnectedAccount({ closeCb }: ModalProps) {
  const { activeAddress, connectedActiveAccounts, providers, activeAccount } =
    useWallet();

  const activeProvider = providers?.find(
    (provider) => provider.metadata.id === activeAccount?.providerId
  );

  const addresses = connectedActiveAccounts?.map(({ address: a }) => a) ?? [];

  const { isPending: loading1, error: error1, data: listings } = useListings();
  const {
    isPending: loading2,
    error: error2,
    data: sunsetListings,
  } = useListings(undefined, true);

  const loading = loading1 || loading2;
  const error = error1 ?? error2;

  const { data: algoAcctInfo } = useAlgoAcctInfo(activeAddress);
  const userAlgoBalance = useBalance(0, algoAcctInfo);

  return (
    <div className="flex flex-col gap-5 items-center">
      <div className="font-bold text-lg -mb-2">Connected</div>
      <div className="flex gap-2 flex-col items-center">
        <AccountsDropdown
          value={activeAddress ?? ""}
          addresses={addresses}
          setActive={(a) => activeProvider?.setActiveAccount(a?.toUpperCase())}
        />
        <div className="flex justify-between items-center w-full">
          <CopyButton value={activeAddress ?? ""} />
          <Button
            size="sm"
            variant="destructive"
            onClick={() => activeProvider?.disconnect()}
          >
            <img
              className="-ml-2 rounded-full"
              width={26}
              height={26}
              alt={`${activeProvider?.metadata.name} icon`}
              src={activeProvider?.metadata.icon}
            />
            DISCONNECT
          </Button>
        </div>
      </div>
      <div className="font-bold text-lg -mb-2">Balances</div>
      <div>
        <WithErrorAndLoading
          mini={true}
          loading={loading || !algoAcctInfo}
          error={error}
        >
          <div className="flex flex-col gap-1 group text-muted-foreground transition-all w-80">
            <div className="flex justify-between" key="bal-ALGO">
              <div className="flex items-center gap-2">
                <SingleToken variant="xs" glowing={false} />
                ALGO
              </div>
              <div>{formatNumber(userAlgoBalance, 6, 2)}</div>
            </div>

            {(algoAcctInfo?.assets ?? [])
              .sort(({ amount: a }, { amount: b }) => (a < b ? 1 : -1))
              .map(({ assetId: assetIndex, amount: balance }) => {
                if (balance <= 0) return;
                const listing = findListingByLstId(
                  listings!,
                  BigInt(assetIndex)
                );
                const sunsetListing = findListingByLstId(
                  sunsetListings!,
                  BigInt(assetIndex)
                );
                const assetName = listing?.lstName ?? sunsetListing?.lstName;
                const sunset = !!sunsetListing;
                if (assetName) {
                  return (
                    <div
                      className="flex justify-between"
                      key={`bal-${assetName}`}
                    >
                      <div className="">
                        <Link
                          onClick={closeCb}
                          className="flex hover:text-foreground items-center gap-1"
                          to={`/dualSTAKE/${assetName}`}
                        >
                          <Token
                            variant="xs"
                            className="mr-1"
                            leftAssetId={Number(listing?.asaId)}
                            rightAssetId={0}
                            glowing={false}
                          />
                          {assetName}
                          {sunset ? (
                            <Sunset className="text-orange-500" size={16} />
                          ) : null}{" "}
                          <ExternalLink size={16} />
                        </Link>
                      </div>
                      <div>{formatNumber(balance, 6)}</div>
                    </div>
                  );
                }
              })
              .filter(Boolean)}
          </div>
        </WithErrorAndLoading>
      </div>
    </div>
  );
}

function ConnectPrompt({ closeCb }: ModalProps) {
  const { providers } = useWallet();
  return (
    <div className="flex flex-col items-center gap-3 mb-1">
      <div className="font-bold text-lg mt-1 mb-3">Connect Wallet</div>
      <div className="flex w-[380px] justify-center items-center flex-wrap gap-4">
        {providers?.map((provider) => (
          <div key={provider.metadata.id}>
            <Button
              variant="secondary"
              className="h-28 pt-4 w-28 relative flex rounded-xl flex-col justify-center items-center gap-1 bg-card text-card-foreground border"
              onClick={() => {
                closeCb();
                provider.connect();
              }}
            >
              <img
                className="rounded-full w-[48px] h-[48px]"
                width={48}
                height={48}
                alt={`${provider.metadata.name} icon`}
                src={provider.metadata.icon}
              />
              <span>{provider.metadata.name}</span>
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

type Fn = () => void;

export function AccountButton({
  className,
  avatarClassName,
  onClick,
}: {
  className?: string;
  avatarClassName?: string;
  onClick?: Fn;
}) {
  const { activeAddress } = useWallet();

  const isConnected = !!activeAddress;
  const { data: avatar } = useNFDAvatar(activeAddress);

  return avatar ? (
    <Button
      variant="ghost"
      className={twMerge("ml-0 md:ml-4 p-0 px-2", avatarClassName)}
      onClick={onClick!}
    >
      <img src={avatar} width={40} height={40} className="rounded-[100%]" />
    </Button>
  ) : (
    <Button
      onClick={onClick!}
      className={twMerge("ml-0 md:ml-4", className)}
      variant={isConnected ? "secondary" : "default"}
      size="icon"
    >
      {isConnected ? <UserCheckIcon /> : <NoUserIcon />}
    </Button>
  );
}

export function AccountSheet() {
  const { open, setOpen } = useContext(AccountSheetContext);
  const { activeAccount } = useWallet();

  const isConnected = activeAccount?.address;
  const closeCb = useCallback(() => setOpen(false), [setOpen]);

  return (
    <>
      <Sheet open={open} onOpenChange={setOpen}>
        {/* This button will trigger open the mobile sheet menu */}
        <SheetTrigger asChild>
          <AccountButton className="px-7" />
        </SheetTrigger>

        <SheetContent side="top">
          {isConnected ? (
            <ConnectedAccount closeCb={closeCb} />
          ) : (
            <ConnectPrompt closeCb={closeCb} />
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
